/**
 * Family-local layout for the bostad family (bostadshyra, studentbostad and
 * the clean tilläggsavtal). Everything generic — slots, options, values,
 * party sections, signing, custom sections, bilaga references — comes from
 * `common/`; only the bostad section frame, the unnumbered `ClauseBlock`,
 * the printed tick box and the inline label/value grid live here.
 */
import { checkbox, list, nodes, p, section } from '@hyranai/regta-document-ir';
import { fieldRows } from '../common/fields.js';
import { ticked } from '../common/slots.js';
/** The option with this code; throws at module load on a typo. */
export function optionOf(options, code) {
    const found = options.find((o) => o.code === code);
    if (!found)
        throw new Error(`unknown option code '${code}'`);
    return found;
}
/** "☑"/"☐" — a printed tick box bound to its form field. Unticked on a blank form. */
export const box = (ctx, checked, sourceKey) => checkbox(ticked(ctx, checked), sourceKey, '☑');
/** The legacy `FieldRow` grid in the bostad family's inline variant. Falsy rows drop out. */
export const rows = (entries) => nodes(fieldRows(entries, { variant: 'inline' }));
/**
 * One auto-numbered `§` section whose body a spec computes. Instance input
 * `beside_previous: true` lays it beside the previous section (legacy
 * `grid grid-cols-2 gap-6` around two `<Section>`s).
 */
export function sectionBlock(spec) {
    return {
        blockKey: spec.blockKey,
        version: spec.version ?? 'v1',
        plane: spec.plane ?? 'legal',
        status: 'published',
        language: 'sv-SE',
        title: spec.title,
        summary: spec.summary,
        ...(spec.legalReview ? { legalReview: spec.legalReview } : {}),
        slots: spec.slots,
        compile: (input, ctx) => {
            const body = spec.body(input, ctx);
            if (body == null)
                return null;
            const out = [
                section(spec.ref, spec.title, body, {
                    keepTogether: true,
                    ...(spec.numbering === 'none' ? { numbering: 'none' } : {}),
                    ...(spec.variant ? { variant: spec.variant } : {}),
                    ...(input.beside_previous === true ? { sideBySide: true } : {}),
                }),
            ];
            const attachments = spec.attachments?.(input, ctx) ?? [];
            return attachments.length ? { nodes: out, attachments } : out;
        },
    };
}
/**
 * A titled, unnumbered static clause — the legacy `ClauseBlock`: a run-in
 * bold title over its paragraphs; bullets are a real list so each one is its
 * own editable node.
 */
export function unnumberedClause(spec) {
    const body = spec.body.map((part) => (typeof part === 'string' ? p(part) : list([...part.bullets])));
    return sectionBlock({
        ...spec,
        slots: [],
        numbering: 'none',
        variant: 'clause',
        body: () => body,
    });
}
//# sourceMappingURL=kit.js.map